import {
  INTEGRATIONS_FETCH_STATUS_REQUEST,
  INTEGRATIONS_FETCH_STATUS_SUCCESS,
  INTEGRATIONS_FETCH_STATUS_FAILURE,
  INTEGRATIONS_DISCONNECT_REQUEST,
  INTEGRATIONS_DISCONNECT_SUCCESS,
  INTEGRATIONS_DISCONNECT_FAILURE,
  INTEGRATIONS_SET_CONNECTION_STATUS,
  INTEGRATIONS_FETCH_EMAILS_REQUEST,
  INTEGRATIONS_FETCH_EMAILS_SUCCESS,
  INTEGRATIONS_FETCH_EMAILS_FAILURE,
  INTEGRATIONS_FETCH_MORE_EMAILS_REQUEST,
  INTEGRATIONS_FETCH_MORE_EMAILS_SUCCESS,
  INTEGRATIONS_FETCH_MORE_EMAILS_FAILURE,
  INTEGRATIONS_FETCH_FOLDER_COUNTS_SUCCESS,
  INTEGRATIONS_FETCH_EMAIL_DETAIL_REQUEST,
  INTEGRATIONS_FETCH_EMAIL_DETAIL_SUCCESS,
  INTEGRATIONS_FETCH_EMAIL_DETAIL_FAILURE,
  INTEGRATIONS_CLEAR_EMAIL_DETAIL,
  INTEGRATIONS_MARK_EMAIL_READ,
  INTEGRATIONS_RESET_STATE,
} from '@core/constants/actions';
import { IntegrationsState } from '@core/types/state';

export const initialState: IntegrationsState = {
  connection: {
    status: 'none',
    accountEmail: null,
    isLoading: true,
    errorCode: null,
  },
  emails: {
    items: [],
    nextLink: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    unreadCount: 0,
  },
  emailDetail: {
    detail: null,
    isLoading: false,
    error: null,
  },
};

const integrationsReducer = (
  state: IntegrationsState = initialState,
  action: any
): IntegrationsState => {
  switch (action.type) {
    // ---------------- Connection Status ----------------
    case INTEGRATIONS_FETCH_STATUS_REQUEST:
      return {
        ...state,
        connection: {
          ...state.connection,
          isLoading: true,
          errorCode: null,
        },
      };

    case INTEGRATIONS_FETCH_STATUS_SUCCESS:
      return {
        ...state,
        connection: {
          status: action.status,
          accountEmail: action.accountEmail ?? null,
          isLoading: false,
          errorCode: null,
        },
      };

    case INTEGRATIONS_FETCH_STATUS_FAILURE:
      return {
        ...state,
        connection: {
          ...state.connection,
          isLoading: false,
          errorCode: action.error || 'connect_failed',
        },
      };

    case INTEGRATIONS_SET_CONNECTION_STATUS:
      return {
        ...state,
        connection: {
          status: action.status,
          accountEmail: action.accountEmail ?? state.connection.accountEmail,
          isLoading: false,
          errorCode: null,
        },
      };

    case INTEGRATIONS_DISCONNECT_REQUEST:
      return {
        ...state,
        connection: {
          ...state.connection,
          isLoading: true,
          errorCode: null,
        },
      };

    case INTEGRATIONS_RESET_STATE:
    case INTEGRATIONS_DISCONNECT_SUCCESS:
      return {
        ...state,
        connection: {
          status: 'none',
          accountEmail: null,
          isLoading: false,
          errorCode: null,
        },
        emails: {
          items: [],
          nextLink: null,
          isLoading: false,
          isLoadingMore: false,
          error: null,
          unreadCount: 0,
        },
        emailDetail: {
          detail: null,
          isLoading: false,
          error: null,
        },
      };

    case INTEGRATIONS_DISCONNECT_FAILURE:
      return {
        ...state,
        connection: {
          ...state.connection,
          isLoading: false,
          errorCode: action.error || 'disconnect_failed',
        },
      };

    // ---------------- Emails List (Initial / Refresh) ----------------
    case INTEGRATIONS_FETCH_EMAILS_REQUEST:
      return {
        ...state,
        emails: {
          ...state.emails,
          isLoading: true,
          error: null,
        },
      };

    case INTEGRATIONS_FETCH_EMAILS_SUCCESS:
      return {
        ...state,
        emails: {
          ...state.emails,
          items: action.emails,
          nextLink: action.nextLink ?? null,
          isLoading: false,
          error: null,
        },
      };

    case INTEGRATIONS_FETCH_EMAILS_FAILURE:
      return {
        ...state,
        emails: {
          ...state.emails,
          isLoading: false,
          error: action.error || 'Failed to fetch emails',
        },
      };

    // ---------------- Emails Pagination (Next Page) ----------------
    case INTEGRATIONS_FETCH_MORE_EMAILS_REQUEST:
      return {
        ...state,
        emails: {
          ...state.emails,
          isLoadingMore: true,
        },
      };

    case INTEGRATIONS_FETCH_MORE_EMAILS_SUCCESS:
      return {
        ...state,
        emails: {
          ...state.emails,
          items: [...state.emails.items, ...action.emails],
          nextLink: action.nextLink ?? null,
          isLoadingMore: false,
        },
      };

    case INTEGRATIONS_FETCH_MORE_EMAILS_FAILURE:
      return {
        ...state,
        emails: {
          ...state.emails,
          isLoadingMore: false,
        },
      };

    // ---------------- Folder Unread Counts ----------------
    case INTEGRATIONS_FETCH_FOLDER_COUNTS_SUCCESS:
      return {
        ...state,
        emails: {
          ...state.emails,
          unreadCount: action.unreadCount,
        },
      };

    // ---------------- Email Detail ----------------
    case INTEGRATIONS_FETCH_EMAIL_DETAIL_REQUEST:
      return {
        ...state,
        emailDetail: {
          detail: null,
          isLoading: true,
          error: null,
        },
      };

    case INTEGRATIONS_FETCH_EMAIL_DETAIL_SUCCESS:
      return {
        ...state,
        emailDetail: {
          detail: action.email,
          isLoading: false,
          error: null,
        },
      };

    case INTEGRATIONS_FETCH_EMAIL_DETAIL_FAILURE:
      return {
        ...state,
        emailDetail: {
          detail: null,
          isLoading: false,
          error: action.error || 'Failed to fetch email detail',
        },
      };

    case INTEGRATIONS_CLEAR_EMAIL_DETAIL:
      return {
        ...state,
        emailDetail: {
          detail: null,
          isLoading: false,
          error: null,
        },
      };

    // ---------------- Mark Email Read ----------------
    case INTEGRATIONS_MARK_EMAIL_READ: {
      const targetItem = state.emails.items.find(
        (e) => e.id === action.emailId
      );
      const isUnread = targetItem ? !targetItem.isRead : false;

      return {
        ...state,
        emails: {
          ...state.emails,
          items: state.emails.items.map((e) =>
            e.id === action.emailId ? { ...e, isRead: true } : e
          ),
          unreadCount: isUnread
            ? Math.max(0, state.emails.unreadCount - 1)
            : state.emails.unreadCount,
        },
        emailDetail:
          state.emailDetail.detail &&
          state.emailDetail.detail.id === action.emailId
            ? {
                ...state.emailDetail,
                detail: {
                  ...state.emailDetail.detail,
                  isRead: true,
                },
              }
            : state.emailDetail,
      };
    }

    default:
      return state;
  }
};

export default integrationsReducer;
